/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import React from "react";

import { BinarySelectQuestionCard } from "./BinarySelectQuestionCard";
import { FreeTextQuestionCard } from "./FreeTextQuestionCard";
import { RankedSelectQuestionCard } from "./RankedSelectQuestionCard";
import { commonStyles } from "./chartHelper";
import { createPurchaserQuestionnaireData } from "./purchaserQuestionnaireData";

import type { PurchaserQuestionnaireQuestionData } from "./purchaserQuestionnaireData";
import type { SalesDashboardEvent } from "./useGetSalesDashboardEvents";
import type { GetTicketQuestionAnswersResponse } from "./useGetTicketQuestionAnswers";

type PurchaserQuestionnaireDashboardProps = {
  data: GetTicketQuestionAnswersResponse | undefined;
  error: unknown;
  events: readonly SalesDashboardEvent[];
  isLoading: boolean;
};

export const PurchaserQuestionnaireDashboard: React.FC<
  PurchaserQuestionnaireDashboardProps
> = ({ data, error, events, isLoading }) => {
  const questions = React.useMemo(
    () => createPurchaserQuestionnaireData(data?.questionAnswers ?? []),
    [data]
  );
  const eventNameById = React.useMemo(
    () => new Map(events.map(({ eventId, eventName }) => [eventId, eventName])),
    [events]
  );
  const splitQuestionIds = React.useMemo(
    () => getSplitQuestionIds(questions),
    [questions]
  );

  if (error) {
    return (
      <PurchaserQuestionnaireDashboardState
        hasError={true}
        message="購入者アンケートデータの取得に失敗しました"
      />
    );
  }

  if (!data) {
    if (isLoading) {
      return <PurchaserQuestionnaireDashboardSkeleton />;
    }

    return (
      <PurchaserQuestionnaireDashboardState
        hasError={false}
        message="データがありません"
      />
    );
  }

  if (questions.length === 0) {
    return (
      <PurchaserQuestionnaireDashboardState
        hasError={false}
        message="データがありません"
      />
    );
  }

  return (
    <div css={styles.questionGrid}>
      {questions.map((question, index) => {
        const eventName = getQuestionEventName({
          eventNameById,
          question,
          splitQuestionIds,
        });
        const key = `${question.questionId}-${question.sourceEventIds.join("-")}-${question.displayType}-${index}`;

        return (
          <div
            css={
              question.displayType === "freeText"
                ? styles.fullWidthQuestion
                : styles.binaryQuestion
            }
            key={key}
          >
            {question.displayType === "binarySelect" ? (
              <BinarySelectQuestionCard
                eventName={eventName}
                question={question}
              />
            ) : question.displayType === "freeText" ? (
              <FreeTextQuestionCard eventName={eventName} question={question} />
            ) : (
              <RankedSelectQuestionCard
                eventName={eventName}
                question={question}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

const getSplitQuestionIds = (
  questions: readonly PurchaserQuestionnaireQuestionData[]
): ReadonlySet<string> => {
  const questionCountById = new Map<string, number>();

  questions.forEach(({ questionId }) => {
    questionCountById.set(
      questionId,
      (questionCountById.get(questionId) ?? 0) + 1
    );
  });

  return new Set(
    Array.from(questionCountById)
      .filter(([, questionCount]) => questionCount > 1)
      .map(([questionId]) => questionId)
  );
};

const getQuestionEventName = ({
  eventNameById,
  question,
  splitQuestionIds,
}: {
  eventNameById: ReadonlyMap<string, string>;
  question: PurchaserQuestionnaireQuestionData;
  splitQuestionIds: ReadonlySet<string>;
}): string | undefined => {
  if (
    !splitQuestionIds.has(question.questionId) ||
    question.sourceEventIds.length === 0
  ) {
    return undefined;
  }

  return question.sourceEventIds
    .map((eventId) => eventNameById.get(eventId) ?? eventId)
    .join(" / ");
};

const PurchaserQuestionnaireDashboardSkeleton: React.FC = () => (
  <div
    css={styles.questionGrid}
    aria-busy="true"
    aria-label="購入者アンケートを読み込み中"
  >
    <div css={[styles.skeletonCard, styles.binaryQuestion]}>
      <div css={commonStyles.skeleton} />
    </div>
    <div css={[styles.skeletonCard, styles.binaryQuestion]}>
      <div css={commonStyles.skeleton} />
    </div>
    <div css={[styles.skeletonCard, styles.fullWidthQuestion]}>
      <div css={commonStyles.skeleton} />
    </div>
  </div>
);

const PurchaserQuestionnaireDashboardState: React.FC<{
  hasError: boolean;
  message: string;
}> = ({ hasError, message }) => (
  <div css={styles.statePanel} role={hasError ? "alert" : undefined}>
    {message}
  </div>
);

const styles = {
  questionGrid: css`
    display: grid;
    grid-template-columns: repeat(
      auto-fit,
      minmax(min(100%, calc(480 / 16 * 1rem)), 1fr)
    );
    gap: 14px;
  `,
  binaryQuestion: css`
    min-width: 0;
  `,
  fullWidthQuestion: css`
    grid-column: 1 / -1;
    min-width: 0;
  `,
  skeletonCard: css`
    height: 320px;
    padding: clamp(16px, 2.5vw, 24px);
    background: #1b2734;
    border: 1px solid #2e3d4c;
    border-radius: 10px;
    box-shadow: 0 1px 2px rgb(0 0 0 / 22%);
  `,
  statePanel: css`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 220px;
    padding: 24px;
    color: #b9c3cd;
    font-size: 14px;
    text-align: center;
    background: #1b2734;
    border: 1px solid #2e3d4c;
    border-radius: 10px;
    box-shadow: 0 1px 2px rgb(0 0 0 / 22%);
  `,
};
